import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import "react-datepicker/dist/react-datepicker.css";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import EmployerCard from "components/EmployerCard";

import {
  addEmployerGroup,
  getAllEmployerGroups,
} from "actions/employerActions";
import { toast } from "react-toastify";
import { employerConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { selectableBanksSelector } from "store/selectors/bank";
import {
  hasEnabledDaysBeforeDebit,
  hasExpiredInvites,
} from "store/selectors/employer";
import GroupForm from "components/GroupForm";
import type { RootState } from "types/state";

const actions = [
  employerConstants.GET_EMPLOYER_GROUPS,
  employerConstants.ADD_EMPLOYER_GROUPS,
];

const isLoading = createLoadingSelector(actions);
const error = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  // `company` is Record<string, unknown> in EmployerState — cast the fields
  // the component consumes at the boundary (PropTypes had companyId: string,
  // planTypes: arrayOf(shape)).
  const companyId = state.employer.company.id as string;

  return {
    // selectableBanksSelector returns UserBank[]; GroupForm consumes a looser
    // GroupAccount[] shape — widen at the boundary (PropTypes had accounts: array).
    accounts: selectableBanksSelector(state) as React.ComponentProps<
      typeof GroupForm
    >["accounts"],
    companyId,
    isLoading: isLoading(state),
    error: error(state),
    planTypes: state.employer.company.planTypes as React.ComponentProps<
      typeof GroupForm
    >["planTypes"],
    hasExpiredInvites: hasExpiredInvites(state),
    enabledDaysBeforeDebit: hasEnabledDaysBeforeDebit(state),
  };
};

const mapDispatchToProps = {
  addEmployerGroup,
  getAllEmployerGroups,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onClose?: () => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

// The group payload accepted by the addEmployerGroup action creator.
type GroupPayload = Parameters<Props["addEmployerGroup"]>[2];

class AddGroup extends React.PureComponent<Props> {
  // Param typed to match GroupForm's onSubmit contract (the value that flows in
  // at runtime); cast to the action creator's narrower payload at the call site.
  submitGroup = (group: Record<string, unknown>) => {
    void this.props
      .addEmployerGroup(
        this.props.client as ApolloClient<object>,
        this.props.companyId,
        group as GroupPayload
      )
      .then((result) => {
        if (result.success) {
          toast.success("Successfully added group.");
          void this.props.getAllEmployerGroups(
            this.props.client as ApolloClient<object>
          );
          this.props.onClose!();
        }
      });
  };

  _cardTitle = () => {
    return "Add Group";
  };

  _cardDescription = () => {
    return "A group is the way that you can align your Icon payroll withdrawals with your actual payroll schedule per payroll group.";
  };

  render() {
    return (
      <EmployerCard
        title={this._cardTitle()}
        description={this._cardDescription()}
        content={
          <GroupForm
            onClose={this.props.onClose}
            onSubmit={this.submitGroup}
            // @ts-expect-error inert pre-existing prop: GroupForm does not
            // declare or read companyId; preserved to keep JSX output unchanged.
            companyId={this.props.companyId}
            error={this.props.error}
            accounts={this.props.accounts}
            isLoading={this.props.isLoading}
            planTypes={this.props.planTypes}
            hasExpiredInvites={this.props.hasExpiredInvites}
            enabledDaysBeforeDebit={this.props.enabledDaysBeforeDebit}
          />
        }
      />
    );
  }
}

export default connector(withApollo<Props>(AddGroup));
